import { useEffect } from "react";
import { Box, Button, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useSearchParams } from "react-router-dom";
import { isProductionSiteHost, resolveOAuthApiBaseUrl } from "config";
import { getSafeRedirectPath } from "utils/navigation";
import { pageFonts } from "styles/main_style";

const PROVIDERS = [
  { id: "google", label: "Continue with Google" },
  { id: "linkedin", label: "Continue with LinkedIn" },
  { id: "facebook", label: "Continue with Facebook" },
] as const;

function ProviderIcon({ provider }: { provider: (typeof PROVIDERS)[number]["id"] }) {
  if (provider === "google") {
    return (
      <Box component="svg" viewBox="0 0 24 24" sx={{ width: 18, height: 18 }} aria-hidden>
        <path
          fill="#4285F4"
          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
        />
        <path
          fill="#34A853"
          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        />
        <path
          fill="#FBBC05"
          d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
        />
        <path
          fill="#EA4335"
          d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        />
      </Box>
    );
  }

  if (provider === "linkedin") {
    return (
      <Box component="svg" viewBox="0 0 24 24" sx={{ width: 18, height: 18 }} aria-hidden>
        <path
          fill="#0A66C2"
          d="M20.45 20.45h-3.55v-5.57c0-1.33-.03-3.04-1.85-3.04-1.85 0-2.14 1.45-2.14 2.94v5.67H9.35V9h3.41v1.56h.05c.47-.9 1.64-1.85 3.37-1.85 3.6 0 4.27 2.37 4.27 5.46v6.28zM5.34 7.43a2.06 2.06 0 1 1 0-4.12 2.06 2.06 0 0 1 0 4.12zM7.12 20.45H3.56V9h3.56v11.45z"
        />
      </Box>
    );
  }

  return (
    <Box component="svg" viewBox="0 0 24 24" sx={{ width: 18, height: 18 }} aria-hidden>
      <path
        fill="#1877F2"
        d="M24 12.07C24 5.4 18.63 0 12 0S0 5.4 0 12.07C0 18.1 4.39 23.09 10.13 24v-8.44H7.08v-3.49h3.05V9.41c0-3.02 1.79-4.7 4.54-4.7 1.31 0 2.69.24 2.69.24v2.97h-1.52c-1.5 0-1.96.93-1.96 1.89v2.26h3.34l-.53 3.49h-2.81V24C19.61 23.09 24 18.1 24 12.07z"
      />
    </Box>
  );
}

export default function SocialAuthButtons() {
  const [searchParams] = useSearchParams();
  const redirectPath = getSafeRedirectPath(searchParams.get("redirect"));
  const apiBase = resolveOAuthApiBaseUrl().replace(/\/$/, "");

  useEffect(() => {
    if (typeof window === "undefined" || !isProductionSiteHost(window.location.hostname)) {
      return;
    }
    void fetch("/api/warmup").catch(() => undefined);
  }, []);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25, mb: 2.5 }}>
      {PROVIDERS.map((provider) => {
        const startUrl = new URL(`${apiBase}/auth/external/${provider.id}/start`);
        startUrl.searchParams.set("client", "web");
        if (redirectPath) {
          startUrl.searchParams.set("redirect", redirectPath);
        }

        return (
          <Button
            key={provider.id}
            href={startUrl.toString()}
            fullWidth
            variant="outlined"
            startIcon={<ProviderIcon provider={provider.id} />}
            sx={{
              py: 1.15,
              fontFamily: pageFonts.sans,
              fontWeight: 700,
              fontSize: "0.875rem",
              letterSpacing: "0.04em",
              textTransform: "none",
              borderRadius: 1.25,
              color: "#1e293b",
              bgcolor: alpha("#ffffff", 0.72),
              borderColor: alpha("#475569", 0.35),
              "&:hover": {
                bgcolor: alpha("#ffffff", 0.92),
                borderColor: alpha("#475569", 0.55),
              },
            }}
          >
            {provider.label}
          </Button>
        );
      })}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          mt: 0.5,
        }}
      >
        <Box sx={{ flex: 1, height: "1px", bgcolor: alpha("#64748b", 0.28) }} />
        <Typography
          variant="caption"
          sx={{
            fontFamily: pageFonts.mono,
            fontWeight: 600,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            color: "#64748b",
          }}
        >
          or
        </Typography>
        <Box sx={{ flex: 1, height: "1px", bgcolor: alpha("#64748b", 0.28) }} />
      </Box>
    </Box>
  );
}
